import AdressesGouv from './adressesgouv';

const adressesGouv = new AdressesGouv();

/**
 * Service de géocodage pour convertir les adresses en coordonnées GPS
 * Utilise l'API Adresse du gouvernement français (gratuite, sans limite)
 */
const geocodingService = {
  /**
   * Géocode une adresse unique
   * @param {string} adresse - L'adresse à géocoder (ex: "10 rue de la Paix")
   * @param {string} codePostal - Le code postal
   * @param {string} ville - La ville
   * @returns {Promise<{lat: number, lon: number}|null>} Les coordonnées ou null si non trouvé
   */
  async geocodeAdresse(adresse, codePostal, ville) {
    try {
      const query = [adresse, codePostal, ville].filter(Boolean).join(' ');

      if (!query.trim()) {
        return null;
      }

      const response = await adressesGouv.search({
        q: query,
        limit: 1,
      });

      if (response.data?.features?.length > 0) {
        const [lon, lat] = response.data.features[0].geometry.coordinates;
        return { lat, lon };
      }

      return null;
    } catch (error) {
      console.error('Erreur géocodage:', error);
      return null;
    }
  },

  /**
   * Géocode un client unique
   * @param {Object} client - Le client avec adresse, codeP, ville
   * @returns {Promise<{id: number, lat: number, lon: number}|null>} Le client avec coordonnées ou null
   */
  async geocodeClient(client) {
    const coords = await this.geocodeAdresse(
      client.adr1 || client.adresse,
      client.codeP || client.code_postal,
      client.ville
    );

    if (coords) {
      return {
        id: client.id,
        lat: coords.lat,
        lon: coords.lon,
      };
    }

    return null;
  },

  /**
   * Géocode une liste de clients qui n'ont pas de coordonnées GPS
   * @param {Array} clients - Liste des clients avec adresse, codeP, ville, lat, lon
   * @param {Function} onProgress - Callback appelé à chaque client traité (index, total)
   * @param {Function} onClientGeocoded - Callback appelé quand un client est géocodé (client, coords)
   * @returns {Promise<Array>} Liste des clients avec leurs nouvelles coordonnées
   */
  async geocodeClients(clients, onProgress = null, onClientGeocoded = null) {
    const results = [];
    const clientsToGeocode = clients.filter(
      (c) => !c.lat || !c.lon
    );

    for (let i = 0; i < clientsToGeocode.length; i++) {
      const client = clientsToGeocode[i];

      if (onProgress) {
        onProgress(i + 1, clientsToGeocode.length);
      }

      const coords = await this.geocodeAdresse(
        client.adr1 || client.adresse,
        client.codeP || client.code_postal,
        client.ville
      );

      if (coords) {
        const result = {
          id: client.id,
          lat: coords.lat,
          lon: coords.lon,
        };
        results.push(result);

        if (onClientGeocoded) {
          onClientGeocoded(client, coords);
        }
      }

      // Pause de 100ms entre chaque requête pour ne pas surcharger l'API
      if (i < clientsToGeocode.length - 1) {
        await this.delay(100);
      }
    }

    return results;
  },

  /**
   * Enrichit une liste de clients avec les coordonnées GPS manquantes
   * @param {Array} clients - Liste des clients
   * @param {Function} onProgress - Callback de progression
   * @returns {Promise<Array>} Liste des clients enrichis
   */
  async enrichClientsWithCoords(clients, onProgress = null) {
    const enrichedClients = [...clients];
    const clientsMap = new Map(enrichedClients.map((c, i) => [c.id, i]));

    const geocodedResults = await this.geocodeClients(
      clients,
      onProgress
    );

    for (const result of geocodedResults) {
      const index = clientsMap.get(result.id);
      if (index !== undefined) {
        enrichedClients[index] = {
          ...enrichedClients[index],
          lat: result.lat,
          lon: result.lon,
        };
      }
    }

    return enrichedClients;
  },

  /**
   * Utilitaire pour créer une pause
   * @param {number} ms - Durée en millisecondes
   */
  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  },
};

export default geocodingService;
